/** Parse "YYYY-MM-DD" + "HH:mm" into a Date for sorting */
function ts(job) {
  return new Date(`${job.job_date}T${job.start_time}:00`).getTime();
}

/** Sort jobs ascending by job_date, then start_time */
export function sortJobsByDateTimeAsc(a, b) {
  if (a.job_date !== b.job_date) return new Date(a.job_date) - new Date(b.job_date);
  const [hA, mA] = a.start_time.split(':').map(Number);
  const [hB, mB] = b.start_time.split(':').map(Number);
  return hA !== hB ? hA - hB : mA - mB;
}

/**
 * Group by endorsement_form_id (used by ManageEndorsementForm & ViewDailyForecast).
 * - Keeps technicians, vehicle, dispatch_time, driver from the *first* seen item in the group
 * - Normalizes job_types to an array
 * - Sorts jobs in each group chronologically
 * - Sorts groups by each group’s earliest job
 */
export function groupByEndorsementIdAndSort(jobs) {
  const groups = {};
  for (const job of jobs) {
    const id = job.endorsement_form_id;
    if (!id) continue;

    if (!groups[id]) {
      groups[id] = {
        endorsement_form_id: id,
        // carry these fields if present
        technicians: job.technicians || [],
        endorsement_vehicle: job.endorsement_vehicle,
        endorsement_dispatch_time: job.endorsement_dispatch_time,
        endorsement_form_driver: job.endorsement_form_driver,
        jobOrders: [],
      };
    }
    groups[id].jobOrders.push({
      ...job,
      job_types: Array.isArray(job.job_types) ? job.job_types : [],
    });
  }

  // sort jobs in each group
  Object.values(groups).forEach((g) => g.jobOrders.sort(sortJobsByDateTimeAsc));

  // sort groups by earliest job
  const arr = Object.values(groups);
  arr.sort((g1, g2) => ts(g1.jobOrders[0]) - ts(g2.jobOrders[0]));
  return arr;
}

/** util: format YYYY-MM-DD -> dd-MMM-yyyy (e.g., 05-Oct-2025) */
function formatJobDateToDDMMMYYYY(dateString) {
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${day}-${monthNames[d.getMonth()]}-${d.getFullYear()}`;
}

/** util: "HH:mm" -> "h:mm AM/PM" */
function formatAmPm(timeStr) {
  if (!timeStr) return "";
  const [hourStr, minute] = timeStr.split(":");
  let h = parseInt(hourStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${minute} ${ampm}`;
}

/**
 * Group by unique technician groups for *creation* (CreateEndorsementForm).
 * - Excludes any job with endorsement_form_id (already inside an endorsement)
 * - Normalizes job_types
 * - Formats job_date as dd-MMM-yyyy and times to AM/PM (to match current UI)
 * - Sorts each group’s jobs chronologically
 *
 * Returns: [{ technicians, jobOrders }]
 */
export function groupByTechnicianGroupsForCreation(jobs) {
  const byKey = {};

  for (const job of jobs) {
    if (job.endorsement_form_id) continue; // exclude already-endorsed

    const key = (job.technicians || [])
      .map((t) => t.id)
      .sort()
      .join("-");

    const updated = {
      ...job,
      job_types: Array.isArray(job.job_types) ? job.job_types : [],
      job_date: formatJobDateToDDMMMYYYY(job.job_date),
      start_time: formatAmPm(job.start_time),
      end_time: formatAmPm(job.end_time),
    };

    if (!byKey[key]) {
      byKey[key] = {
        technicians: job.technicians || [],
        jobOrders: [],
      };
    }
    byKey[key].jobOrders.push(updated);
  }

  const groups = Object.values(byKey);
  groups.forEach((g) => g.jobOrders.sort((a, b) => {
    // same chronological sort as UI (date then time)
    const [dA, mA, yA] = a.job_date.split("-"); // dd-MMM-yyyy
    const [dB, mB, yB] = b.job_date.split("-");
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const dateA = new Date(Number(yA), monthNames.indexOf(mA), Number(dA));
    const dateB = new Date(Number(yB), monthNames.indexOf(mB), Number(dB));
    if (dateA - dateB !== 0) return dateA - dateB;

    const toMinutes = (s) => {
      const [time, mer] = s.split(" ");
      let [hh, mm] = time.split(":").map(Number);
      if (mer === "PM" && hh !== 12) hh += 12;
      if (mer === "AM" && hh === 12) hh = 0;
      return hh * 60 + mm;
    };
    return toMinutes(a.start_time) - toMinutes(b.start_time);
  }));

  return groups;
}
